import { router } from 'expo-router'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { Api } from '@/src/store/api'
import { DateTime } from 'luxon'
import { z } from 'zod/v4'
import * as Notifications from 'expo-notifications'

const REQUESTED_NOTIFICATION_AT = 'requested_notification_at'

const getIgnoredNotificationPermissionRequestAt = async () => {
  const now = DateTime.now()
  try {
    const ignoredNotificationPermissionRequestAt =
      await AsyncStorage.getItem(REQUESTED_NOTIFICATION_AT)

    const isMoreThan1MonthSinceLastRequest = ignoredNotificationPermissionRequestAt
      ? DateTime.fromISO(ignoredNotificationPermissionRequestAt).plus({ months: 1 }) < now
      : true
    return isMoreThan1MonthSinceLastRequest
  } catch {
    return false
  }
}

export const NotificationsApi = Api.enhanceEndpoints({
  addTagTypes: ['getNotificationsPermissions', 'isMoreThan1MonthSinceLastNotificationRequest'],
}).injectEndpoints({
  endpoints: (build) => ({
    getNotificationsPermissions: build.query({
      async queryFn() {
        const getNotificationsPermissions = await Notifications.getPermissionsAsync()

        const isNotificationGranted =
          getNotificationsPermissions.granted ||
          getNotificationsPermissions.ios?.status ===
            Notifications.IosAuthorizationStatus.PROVISIONAL

        const isMoreThan1MonthSinceLastRequest = await getIgnoredNotificationPermissionRequestAt()

        return {
          data: {
            granted: isNotificationGranted,
            canAskAgain: getNotificationsPermissions.canAskAgain,
            status: getNotificationsPermissions.status,
            isMoreThan1MonthSinceLastRequest,
          },
        }
      },
      argSchema: z.void(),
      providesTags: ['getNotificationsPermissions'],
    }),
    requestNotificationsPermissions: build.mutation({
      async queryFn() {
        try {
          const requestNotificationsPermissions = await Notifications.requestPermissionsAsync({
            ios: {
              allowAlert: true,
              allowBadge: true,
              allowSound: true,
            },
          })

          const isNotificationGranted =
            requestNotificationsPermissions.granted ||
            requestNotificationsPermissions.ios?.status ===
              Notifications.IosAuthorizationStatus.PROVISIONAL

          return {
            data: {
              granted: isNotificationGranted,
              canAskAgain: requestNotificationsPermissions.canAskAgain,
              status: requestNotificationsPermissions.status,
            },
          }
        } catch (error) {
          return { error }
        }
      },
      async onQueryStarted(_, startedApi) {
        await startedApi.queryFulfilled
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        router.back()
      },
      argSchema: z.void(),
      invalidatesTags: ['getNotificationsPermissions'],
    }),
    ignoreNotificationPermissionRequestAt: build.mutation({
      async queryFn() {
        const now = DateTime.now()
        try {
          await AsyncStorage.setItem(REQUESTED_NOTIFICATION_AT, now.toISO())
          return { data: {} }
        } catch (error) {
          return { error }
        }
      },
      async onQueryStarted(_, startedApi) {
        await startedApi.queryFulfilled
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        router.back()
      },
      argSchema: z.void(),
      invalidatesTags: ['getNotificationsPermissions'],
    }),
  }),
})
